import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';
import { css, Theme } from '@emotion/react';
import { StickyMessageInterface } from 'types/types';
import RichText from 'components/rich-text';
import Button from 'components/button';
import { X as XIcon } from 'react-feather';
import { Box } from 'components/box';
import { dismissMessagePopup } from '../../redux/global/actions';
import { SectionGroupEnum } from 'types/types';
import { zIndices } from 'constants/z-indices';

const defaultStyle = (theme: Theme) => css`
  margin: 0;
  padding: 5px 0;
  margin-right: ${theme.space[3]}px;
  z-index: ${zIndices.STICKY_DISMISSAL_TOAST_MESSAGE};
  text-align: center;
  color: ${theme.colors.information};
  a {
    color: ${theme.colors.link};
    text-decoration: underline;
    font-weight: 600;
  }
`;

const checkoutStyle = (theme: Theme) => css`
  margin: 0;
  padding: 5px 0;
  z-index: ${zIndices.STICKY_DISMISSAL_TOAST_MESSAGE};
  text-align: center;
  color: ${theme.colors.warning};
  a {
    color: ${theme.colors.link};
    text-decoration: underline;
    font-weight: 600;
  }
`;

const groupMessagesBySection = (messages: StickyMessageInterface[]) => {
  const groupedMessages: Record<
    StickyMessageInterface['section'],
    StickyMessageInterface[]
  > = {};
  return messages.reduce((record, message) => {
    record[message.section] = record[message.section] || [];
    record[message.section].push(message);
    return record;
  }, groupedMessages);
};

const mapToSection = (path: string) => {
  let style = defaultStyle;
  let section: SectionGroupEnum | undefined;
  if (path === '/') {
    section = SectionGroupEnum.Home;
  } else if (path.includes('/product')) {
    section = SectionGroupEnum.Product;
  } else if (path.includes('/checkout')) {
    section = SectionGroupEnum.Checkout;
    style = checkoutStyle;
  } else if (path.includes('/my-account')) {
    section = SectionGroupEnum.MyAccount;
  } else if (path.includes('/my-orders')) {
    section = SectionGroupEnum.MyOrders;
  }
  return { style, section };
};

const getSectionMessage = (
  messages: StickyMessageInterface[],
  path: string
) => {
  const { section, style } = mapToSection(path);
  if (!section) return;

  const groupedMessages = groupMessagesBySection(messages);
  const sectionMessages = groupedMessages[section];
  if (!sectionMessages) return;
  return { style, section, messages: sectionMessages };
};

const StickyMessage = ({
  messages = [],
  path,
  isDismissible,
}: {
  messages?: StickyMessageInterface[];
  path: string;
  isDismissible?: boolean;
}) => {
  const dispatch = useDispatch();
  const sectionMessages = getSectionMessage(messages, path);
  const dismissedMessagesBySection = useSelector(
    (state: RootState) => state.global.dismissedMessagesBySection
  );

  if (
    !sectionMessages ||
    sectionMessages.section === dismissedMessagesBySection ||
    sectionMessages.messages.length === 0
  ) {
    return null;
  }

  return (
    <>
      {sectionMessages.messages.map((message, idx) => {
        const color =
          message.section === SectionGroupEnum.Checkout
            ? 'lightOrange'
            : 'lightBlue';

        return (
          <Box
            mx="auto"
            px="2"
            key={idx}
            css={theme => css`
              ${isDismissible &&
              css`
                display: flex;
                justify-content: center;
                max-width: ${theme.maxHeaderWidth}px;
                position: relative;
              `}
            `}
          >
            <RichText
              fontSize={['0.75rem', 'r']}
              content={message.content}
              css={sectionMessages.style}
            />

            {isDismissible && (
              <Button
                onClick={() => dispatch(dismissMessagePopup(message.section))}
                p="0"
                m="0"
                variant="flat"
                color={color}
                css={theme => css`
                  position: absolute;
                  top: 5px;
                  right: ${theme.space[2]}px;
                `}
              >
                <XIcon
                  css={theme => css`
                    color: ${theme.colors.primary};
                  `}
                  size={17}
                />
              </Button>
            )}
          </Box>
        );
      })}
    </>
  );
};

export default StickyMessage;
